import React, { MutableRefObject, useEffect } from 'react';

import useReportViewer from '../../../../../hooks/useReportViewer';
import useGlobalOkDialog from '../../../../../hooks/useGlobalOkDialog';
import { useTypedFastStorage } from '../../../../../hooks/useFastStorage';

import BankDropDown from '../../../../../uiControls/BankDropDown';
import Date from "../../../../../ui/Date";
import LabeledControl from "../../../../../ui/LabeledControl";
import LeftRightSplit from "../../../../../ui/LeftRightSplit";
import SecondaryButton from "../../../../../ui/SecondaryButton";

import { addDays, addMonths, compareIso, getDate, nowDate } from '../../../../../dateUtils';

import { BankReconActivityReport } from "../../../../../ifmsTypes";
import { BankReconHistoryArgs } from '../../../../../fms.report/args';
import { DocumentType } from '../../../../../framework.core/DocumentType';

interface BankRecActRptViewParams {
  id: string | undefined,
  onPopupClose?: () => void,
};

interface BankRecActRptInitViewParams {
  reportOption: BankReconActivityReport,
  reportOptionRef: MutableRefObject<BankReconActivityReport | null>,
  setReportOption: (x: Partial<BankReconActivityReport>) => void,
  onPopupClose?: () => void
};

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function BankReconActivityReportViewImpl(params: BankRecActRptViewParams) {
  const [inStorage, setInStorage, removeFromStorage, inStorageRef] = useTypedFastStorage<BankReconActivityReport>('report-bankreconactivity-report', null);

  useEffect(() => {
      initialize();
      return () => {
        removeFromStorage()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },[]);

  async function initialize() {
    const data = await loadExisting();
    setInStorage({...data})
  }

  async function loadExisting() {
    return {
      bankId: 0, 
      fromDate: addDays(addMonths(nowDate(),-1), 1), 
      toDate: nowDate()
    }
  }

  if (!inStorage) {
    return null;
  }

  return (
    <BankReconActivityReportViewInitImpl
      reportOption={inStorage}
      reportOptionRef={inStorageRef}
      setReportOption={setInStorage}
      onPopupClose={params.onPopupClose}
    />
  );
}

function BankReconActivityReportViewInitImpl(params: BankRecActRptInitViewParams) {
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const { viewReport } = useReportViewer();
                                      
  function onChange(change: Partial<BankReconActivityReport>) {
    params.setReportOption(change);
  }

  async function print(){
    let errorMsg ='';
    const fromDate:string | undefined = getDate(params.reportOption.fromDate)??"";
    const toDate:string | undefined  = getDate(params.reportOption.toDate)??"";
    const bankId:number | undefined  = params.reportOption.bankId || 0;

    if(!fromDate){
      errorMsg = "Please enter valid starting date.";
    }else if(!toDate){
      errorMsg = "Please enter valid ending date.";
    }else if(compareIso(fromDate, toDate) > 0){
      errorMsg = "The ending date is smaller than the starting date.";
    }

    if(errorMsg) {
      await showGlobalOkDialog(<p>{errorMsg}</p>, false, "Error");
      return null;
    }

    const title = "Bank Recon Activity";
    const args = new BankReconHistoryArgs({
      docType: DocumentType.BankReconHistory,
      formCaption: title,
      subject: title,

      userID: '', 
      strDate: fromDate,
      endDate: toDate,
      bankID: bankId,
    })
    viewReport(args, "", true);
  }

  return (
    <div className='report-popup'>
      <div style={{width: '90%', margin: '0 auto'}}>
        <LabeledControl label="Bank Entity" labelSize="60px" alignRight control={
            <BankDropDown value={params.reportOption.bankId} onChange={bank => onChange({bankId: bank?.id || 0})} noBadBank autoSetDefault />
        } />
        <LabeledControl label="Rec.Date" labelSize="60px" alignRight control={
          <LeftRightSplit leftSize="80px" rightSize="calc(100% - 96px)" left={
              <Date value={params.reportOption.fromDate} onChange={fromDate => onChange({fromDate})} noRangeCheck />
            } right={
              <LabeledControl label='~  ' labelSize='50px' alignRight control={
                <Date value={params.reportOption.toDate} onChange={toDate => onChange({toDate})} noRangeCheck />
              } />
            }
          />
        } />
      </div>

      <div className='report-footer'>
        <SecondaryButton size="regular" onClick={() => { print() }}>PREVIEW</SecondaryButton>
        <SecondaryButton size="regular" onClick={params.onPopupClose}>CANCEL</SecondaryButton>
      </div>
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const BankReconActivityReportView = ({onPopupClose}: { onPopupClose?: () => void }) => {
  return (
    <BankReconActivityReportViewImpl id={'BankRecActRptView'} onPopupClose={onPopupClose} key={`BankRecActRptView`}/>
  );
};
export default BankReconActivityReportView;
